/**
 * Daily-session persistence.
 *
 * A daily puzzle is one attempt per day. Once it resolves — won or lost — the
 * day is over for that game: re-entering shows the result rather than a fresh
 * board. While a round is still in progress the game's own state is snapshotted,
 * so backing out to the hub and returning resumes exactly where the player left
 * off instead of restarting.
 *
 * Mirrors the iOS model: `GameStateStore` scoped saves under `daily_{dateKey}`
 * for in-progress state, plus the completion flags `DailyContext` keeps.
 */
import type { GameId } from './games';

export type DailyStatus = 'playing' | 'won' | 'lost';

/** Summary rendered on the terminal screen when a finished round is reopened. */
export interface DailyResult {
  /** One line describing how it went, e.g. "Cracked FAIRER in 4 guesses." */
  subtitle?: string;
  /** The answer, shown when the round was lost. */
  answer?: string;
  /** Game-specific payload for a richer result screen (e.g. Longshot's shot log). */
  detail?: unknown;
  finishedAt: number;
}

interface DailyRecord<S> {
  status: DailyStatus;
  /** In-progress snapshot; dropped once the round resolves. */
  state?: S;
  result?: DailyResult;
}

const VERSION = 'v1';
const key = (gameId: GameId, dateKey: string) => `daily:${VERSION}:${gameId}:${dateKey}`;

function read<S>(gameId: GameId, dateKey: string): DailyRecord<S> | null {
  try {
    const raw = localStorage.getItem(key(gameId, dateKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DailyRecord<S>;
    if (parsed && typeof parsed.status === 'string') return parsed;
    return null;
  } catch {
    // Corrupt or unavailable storage (private mode, quota) — treat as a fresh
    // round rather than blocking play.
    return null;
  }
}

function write<S>(gameId: GameId, dateKey: string, record: DailyRecord<S>): void {
  try {
    localStorage.setItem(key(gameId, dateKey), JSON.stringify(record));
  } catch {
    // Storage full or blocked. Losing persistence is bad but never fatal.
  }
}

export function getStatus(gameId: GameId, dateKey: string): DailyStatus {
  return read(gameId, dateKey)?.status ?? 'playing';
}

export function isFinished(gameId: GameId, dateKey: string): boolean {
  const s = getStatus(gameId, dateKey);
  return s === 'won' || s === 'lost';
}

export function getResult(gameId: GameId, dateKey: string): DailyResult | null {
  return read(gameId, dateKey)?.result ?? null;
}

/** The in-progress snapshot, or null once the round is over. */
export function loadState<S>(gameId: GameId, dateKey: string): S | null {
  const rec = read<S>(gameId, dateKey);
  if (!rec || rec.status !== 'playing') return null;
  return rec.state ?? null;
}

/** Snapshot mid-round progress. No-op once the round has resolved. */
export function saveState<S>(gameId: GameId, dateKey: string, state: S): void {
  const rec = read<S>(gameId, dateKey);
  if (rec && rec.status !== 'playing') return;
  write<S>(gameId, dateKey, { status: 'playing', state });
}

/**
 * Resolve the round. The in-progress snapshot is dropped — the result is what
 * gets shown from here on, and keeping a stale board around would let a reload
 * resurrect a finished game.
 */
export function finish(
  gameId: GameId,
  dateKey: string,
  status: 'won' | 'lost',
  result: Omit<DailyResult, 'finishedAt'> = {},
): void {
  write(gameId, dateKey, {
    status,
    result: { ...result, finishedAt: Date.now() },
  });
}

/** Statuses for every game on a date — powers the hub tiles in one read. */
export function getStatuses(
  gameIds: readonly GameId[],
  dateKey: string,
): Record<string, DailyStatus> {
  const out: Record<string, DailyStatus> = {};
  for (const id of gameIds) out[id] = getStatus(id, dateKey);
  return out;
}

/**
 * Drop records older than `keepDays`. Without this the store grows a few
 * entries per game per day forever.
 */
export function pruneOlderThan(currentDateKey: string, keepDays = 14): void {
  try {
    const cutoff = new Date(currentDateKey);
    if (Number.isNaN(cutoff.getTime())) return;
    cutoff.setDate(cutoff.getDate() - keepDays);
    const cutoffKey = cutoff.toISOString().slice(0, 10);

    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(`daily:${VERSION}:`)) continue;
      const stamp = k.slice(k.lastIndexOf(':') + 1);
      if (/^\d{4}-\d{2}-\d{2}$/.test(stamp) && stamp < cutoffKey) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    // Storage unavailable — nothing to prune.
  }
}
